import React, { useMemo, useRef, useEffect, useState } from 'react';
import '../../styles/select-hover.css';
import { 
  EnhancedSelect as Select, 
  SelectContent, 
  SelectTrigger, 
  SelectValue 
} from "../ui/enhanced-select";
import { Trash2, CheckCircle } from "lucide-react";
import { CustomSelectItem } from './CustomSelectItem';

interface PersonSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  people: string[];
  label: string;
  isOptional?: boolean;
  onOpenChange?: (open: boolean) => void;
  onEnterPress?: (value: string) => void;
  waitForEnter?: boolean;
  portalContainer?: HTMLElement | null;
  showConfirmButton?: boolean;
}

export const PersonSelect = React.memo(({ 
  value, 
  onValueChange, 
  people, 
  label, 
  isOptional = false, 
  onOpenChange,
  onEnterPress,
  waitForEnter = false,
  portalContainer,
  showConfirmButton = false
}: PersonSelectProps) => {
  // Memoize the sorted people list
  const sortedPeople = useMemo(() => [...people].sort(), [people]);
  const selectRef = useRef<HTMLDivElement>(null);
  
  // Track dropdown state
  const [isOpen, setIsOpen] = useState(false);
  const wasOpenRef = useRef(false);
  
  // Track selected value
  const [selectedValue, setSelectedValue] = useState(value);
  
  // Track if the dropdown trigger is focused
  const [isFocused, setIsFocused] = useState(false);
  
  // Track if we're in keyboard selection mode
  const [keyboardMode, setKeyboardMode] = useState(false);
  
  // Track if we're in the process of opening the dropdown via typing
  const [isOpeningViaTyping, setIsOpeningViaTyping] = useState(false);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // If Enter is pressed while dropdown is open and we have a value
      if (e.key === 'Enter' && isOpen && selectedValue && onEnterPress) {
        e.preventDefault();
        setKeyboardMode(true);
        onEnterPress(selectedValue);
        // Reset the value after Enter is pressed
        setSelectedValue('');
        onValueChange('');
        // Reset keyboard mode after a short delay
        setTimeout(() => {
          setKeyboardMode(false);
        }, 100);
      }
      
      // If a letter key is pressed while dropdown is focused but not open,
      // automatically open the dropdown
      if (e.key.length === 1 && isFocused && !isOpen && waitForEnter) {
        // Set keyboard mode
        setKeyboardMode(true);
        
        // Mark that we're opening via typing
        setIsOpeningViaTyping(true);
        
        // Open the dropdown
        setIsOpen(true);
        if (onOpenChange) {
          onOpenChange(true);
        }
        
        // Reset the flag after a short delay
        setTimeout(() => {
          setIsOpeningViaTyping(false);
        }, 100);
      }
    };

    // Add event listener if dropdown is open or focused
    if ((waitForEnter && isOpen) || isFocused) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isFocused, selectedValue, onEnterPress, waitForEnter, onOpenChange, onValueChange]);

  // Update local state when value prop changes
  useEffect(() => {
    setSelectedValue(value);
  }, [value]);
  
  const handleValueChange = (newValue: string) => {
    // Always update the local state
    setSelectedValue(newValue);

    // Always update the parent's value to ensure the dropdown shows the current selection
    onValueChange(newValue);
  };

  const handleOpenChange = (open: boolean) => {
    // Store previous state before updating
    wasOpenRef.current = isOpen;

    // If the dropdown is closing and we have a value, add it
    if (!open && isOpen && selectedValue && onEnterPress && !isOpeningViaTyping && !waitForEnter) {
      onEnterPress(selectedValue);
      // Reset the value after adding
      setTimeout(() => {
        setSelectedValue('');
        onValueChange('');
      }, 0);
    }

    // Update current state
    setIsOpen(open);

    // Call parent handler if provided
    if (onOpenChange) {
      onOpenChange(open);
    }
  };

  const handleClear = () => {
    setSelectedValue('');
    onValueChange('');
  };

  const handleConfirm = () => {
    if (value && onEnterPress) {
      onEnterPress(value);
      // Reset the value after confirming
      setSelectedValue('');
      onValueChange('');
    }
  };

  return (
    <div className="space-y-2" ref={selectRef}>
      <label className="text-sm font-medium dark:text-gray-200">{label}</label>
      <div className="relative w-full">
        <Select
          value={value}
          onValueChange={handleValueChange}
          onOpenChange={handleOpenChange}
        >
          <SelectTrigger 
            className={`dark:bg-gray-800 dark:border-gray-700 dark:text-white ${value ? 'pr-16' : ''}`}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          >
            <SelectValue placeholder="Select person" />
          </SelectTrigger>
          <SelectContent portalContainer={portalContainer} className="select-content-with-hover">
            {sortedPeople.map((person) => (
              <CustomSelectItem 
                key={person} 
                value={person}
              >
                {person}
              </CustomSelectItem>
            ))}
          </SelectContent>
          
        </Select>
        {value && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
            {(showConfirmButton || waitForEnter) && onEnterPress && (
              <button
                type="button"
                onClick={handleConfirm}
                className="p-1 text-gray-500 hover:text-green-500 dark:text-gray-400 dark:hover:text-green-400 transition-colors"
                aria-label="Add to recipients"
                title="Add to recipients"
              >
                <CheckCircle className="h-4 w-4" />
              </button>
            )}
            {isOptional && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
                aria-label="Clear selection"
                title="Clear selection"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

PersonSelect.displayName = 'PersonSelect';
